import React, { useState, useEffect, useCallback, useContext } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Stack from '@mui/material/Stack';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import ImageIcon from '@mui/icons-material/Image';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import { AuthContext } from "../../contexts/auth";
import useUserDetailState from "./../../hooks/useUserDetailState";
import { getPicturePack } from '../../services/getPicturePack';
import postImagePackCheckout from "../../services/stripe/postImagePackCheckout";
import postImagePackSuccess from "../../services/stripe/postImagePackSuccess";

import { style } from "./styles";

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00a271" strokeWidth="3">
        <polyline points="20,6 9,17 4,12" />
    </svg>
);

const ImagePackCard = ({ pack, onBuy, loading, isBestValue }) => {
    const pricePerImage = (pack.price / pack.images).toFixed(3);
    
    return (
        <Card sx={{
            borderRadius: '12px',
            padding: '24px',
            position: 'relative',
            border: isBestValue ? '2px solid #702180' : '1px solid #e0e0e0',
            backgroundColor: '#fff',
            minHeight: '320px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
        }}>
            {isBestValue && (
                <Box sx={{
                    position: 'absolute',
                    top: '-12px',
                    right: '16px',
                    backgroundColor: '#702180',
                    color: 'white',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                }}>
                    BEST VALUE
                </Box>
            )}
            
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Box sx={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: isBestValue ? '#702180' : '#00a271',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                    }}>
                        <ImageIcon sx={{ color: 'white', fontSize: '32px' }} />
                    </Box>
                    <h3 style={{ margin: 0, fontSize: '24px', color: '#333', fontWeight: 'bold' }}>
                        {pack.images} Images
                    </h3>
                </Box>
                
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <span style={{ fontSize: '36px', fontWeight: 'bold', color: '#333' }}>
                        €{pack.price}
                    </span>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ flex: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, justifyContent: 'center' }}>
                        <CheckIcon />
                        <span style={{ fontSize: '14px', color: '#555' }}>
                            {pack.images} AI-generated images
                        </span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, justifyContent: 'center' }}>
                        <LocalOfferIcon sx={{ color: '#00a271', fontSize: '16px' }} />
                        <span style={{ fontSize: '14px', color: '#555' }}>
                            €{pricePerImage} per image
                        </span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <CheckIcon />
                        <span style={{ fontSize: '14px', color: '#555' }}>
                            Never expires
                        </span>
                    </Box>
                </Box>
                
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onBuy(pack)}
                    disabled={loading}
                    data-testid={`button-buy-pack-${pack.id}`}
                    sx={{
                        backgroundColor: isBestValue ? '#702180' : '#00a271',
                        color: 'white',
                        borderRadius: '12px',
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: isBestValue ? '#5a1a66' : '#008a5f',
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        <FormattedMessage id="Buy Now" defaultMessage="Buy Now" />
                    )}
                </Button>
            </CardContent>
        </Card>
    );
};

const Credits = () => {
    const state = useUserDetailState();
    const { user } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buyingPackId, setBuyingPackId] = useState(null);

    const fetchPacks = useCallback(async () => {
        try {
            const result = await getPicturePack('euro');
            if (result.type === 'success' && result.data) {
                const packsArray = Array.isArray(result.data) ? result.data : [];
                setPacks(packsArray);
            } else {
                throw new Error(result.data?.detail || 'Failed to fetch image packs');
            }
        } catch (error) {
            console.error('Error fetching packs:', error);
            toast.error('Failed to load image packs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPacks();
    }, [fetchPacks]);

    useEffect(() => {
        const success = searchParams.get('success');
        const canceled = searchParams.get('canceled');
        const sessionId = searchParams.get('session_id');
        
        if (success === 'true' && sessionId) {
            const processPayment = async () => {
                try {
                    const result = await postImagePackSuccess(sessionId);
                    if (result.success) {
                        toast.success(`Payment successful! ${result.images_added} images have been added to your account.`);
                        if (state.updateUserDetail?.set) {
                            state.updateUserDetail.set();
                        }
                    }
                } catch (error) {
                    console.error('Error processing payment:', error);
                    toast.error('Payment processed but there was an error updating your account. Please contact support.');
                }
            };
            processPayment();
        } else if (canceled === 'true') {
            toast.error('Payment was canceled.');
        }
    }, [searchParams, state]);

    const handleBuy = async (pack) => {
        if (!pack.id) {
            toast.error('Invalid pack');
            return;
        }

        setBuyingPackId(pack.id);
        try {
            const response = await postImagePackCheckout(pack.id);
            if (response.checkout_url) {
                window.location.href = response.checkout_url;
            } else {
                toast.error('Failed to start checkout');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error(error.response?.data?.error || 'Payment error. Please try again.');
        } finally {
            setBuyingPackId(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const sortedPacks = [...packs].sort((a, b) => a.price - b.price);
    const bestValueIndex = sortedPacks.length - 1;

    return (
        <Fade in={true} timeout={500}>
            <Stack sx={style.stack}>
                <Box sx={style.counter}>
                    <Box sx={style.header}>
                        <h1 style={{ 
                            fontSize: '28px', 
                            fontWeight: 'bold', 
                            color: '#333',
                            textAlign: 'center',
                            marginBottom: '8px'
                        }}>
                            <FormattedMessage 
                                id="Get More Images" 
                                defaultMessage="Get More AI Images" 
                            />
                        </h1>
                        <p style={{ 
                            fontSize: '16px', 
                            color: '#666',
                            textAlign: 'center',
                            marginBottom: '16px'
                        }}>
                            <FormattedMessage 
                                id="Purchase image packs" 
                                defaultMessage="Purchase additional images for your AI creations" 
                            />
                        </p>
                        
                        {state.images?.value !== undefined && (
                            <Box sx={{
                                backgroundColor: '#f5f5f5',
                                borderRadius: '12px',
                                padding: '12px 24px',
                                display: 'inline-block',
                                marginBottom: '24px',
                            }}>
                                <span style={{ fontSize: '14px', color: '#666' }}>
                                    <FormattedMessage id="Your current balance" defaultMessage="Your current balance" />:{' '}
                                </span>
                                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#00a271' }}>
                                    {state.images?.value || 0} images
                                </span>
                            </Box>
                        )}
                    </Box>
                    
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '24px',
                        maxWidth: '1000px',
                        margin: '0 auto',
                        padding: '0 16px',
                    }}>
                        {sortedPacks.map((pack, index) => (
                            <ImagePackCard
                                key={pack.id}
                                pack={pack}
                                onBuy={handleBuy}
                                loading={buyingPackId === pack.id}
                                isBestValue={index === bestValueIndex}
                            />
                        ))}
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', mt: 3, color: '#666', fontSize: '14px' }}>
                        <FormattedMessage 
                            id="Secure payment by Stripe" 
                            defaultMessage="Secure payment powered by Stripe" 
                        />
                    </Box>
                </Box>
            </Stack>
        </Fade>
    );
};

export default Credits;
